import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeftIcon } from '@untitledui-icons/react/line'
import { AuthLayout } from '@/components/auth'
import { useAuth } from '@/hooks/api'
import { useAuthStore } from '@/stores/authStore'
import { getErrorMessage } from '@/lib/api'

export function TwoFactorPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  
  const requires2FA = useAuthStore((state) => state.requires2FA)
  const tempToken = useAuthStore((state) => state.tempToken)
  
  const {
    verify2FA,
    isVerifying2FA,
    verify2FAError,
    resend2FA,
    isResending2FA,
    logout,
  } = useAuth()

  // Redirect if no 2FA is required
  useEffect(() => {
    if (!requires2FA || !tempToken) {
      navigate('/login')
    }
  }, [requires2FA, tempToken, navigate])

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus()
  }, [])

  const handleChange = (index: number, value: string) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return

    const newCode = [...code]
    newCode[index] = value
    setCode(newCode)

    // Move to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }

    // Auto-submit when complete
    if (value && index === 5) {
      const fullCode = newCode.join('')
      if (fullCode.length === 6) {
        verify2FA({ code: fullCode })
      }
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Move to previous input on backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    
    if (pastedData.length === 6) {
      const newCode = pastedData.split('')
      setCode(newCode)
      verify2FA({ code: pastedData })
    }
  }

  const handleResend = () => {
    resend2FA()
  }

  const handleCancel = () => {
    logout()
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const fullCode = code.join('')
    if (fullCode.length === 6) {
      verify2FA({ code: fullCode })
    }
  }

  return (
    <AuthLayout
      title={t('app.auth.verify_2fa')}
      subtitle={t('app.auth.enter_verification_code')}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* OTP input */}
        <div className="flex justify-center gap-3">
          {code.map((digit, index) => (
            <input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              disabled={isVerifying2FA}
              className={`
                h-14 w-12 rounded-lg border text-center text-xl font-semibold
                focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-0
                disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500
                dark:bg-gray-800 dark:text-white
                ${
                  verify2FAError
                    ? 'border-error-500 focus:ring-error-500'
                    : 'border-gray-300 dark:border-gray-600'
                }
              `}
            />
          ))}
        </div>

        {/* Error message */}
        {verify2FAError && (
          <div className="rounded-lg bg-error-50 p-3 text-center text-sm text-error-700 dark:bg-error-900/20 dark:text-error-400">
            {getErrorMessage(verify2FAError)}
          </div>
        )}

        {/* Submit button */}
        <button
          type="submit"
          disabled={isVerifying2FA || code.join('').length !== 6}
          className="
            flex w-full items-center justify-center gap-2 rounded-lg
            bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white
            transition-colors hover:bg-brand-600
            focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2
            disabled:cursor-not-allowed disabled:opacity-50
          "
        >
          {isVerifying2FA ? (
            <>
              <svg
                className="h-4 w-4 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {t('resources.generic.loading')}
            </>
          ) : (
            t('app.auth.verify')
          )}
        </button>

        {/* Resend code */}
        <p className="text-center text-sm text-gray-500 dark:text-gray-400">
          {t('app.auth.resend_code')}{' '}
          <button
            type="button"
            onClick={handleResend}
            disabled={isResending2FA}
            className="font-medium text-brand-600 hover:text-brand-700 disabled:opacity-50 dark:text-brand-400"
          >
            {isResending2FA ? t('resources.generic.loading') : t('app.auth.resend_code')}
          </button>
        </p>

        {/* Cancel */}
        <button
          type="button"
          onClick={handleCancel}
          className="flex w-full items-center justify-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          {t('resources.generic.cancel')} {t('app.auth.and')} {t('navigation.logout').toLowerCase()}
        </button>
      </form>
    </AuthLayout>
  )
}

export default TwoFactorPage
